import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  Check,
  CircleDollarSign,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import {
  formatRupeesFromPaise,
  LANDING_CONTACT,
  parseSocietyUnits,
} from "../../config/landingPricing";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const DEFAULT_UNITS = "100";
const DEFAULT_DURATION = 12;

export function LaunchPricingExperience({ onRequestEnquiry }) {
  const [units, setUnits] = useState(DEFAULT_UNITS);
  const [durationMonths, setDurationMonths] = useState(DEFAULT_DURATION);
  const [residentPortal, setResidentPortal] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const parsedUnits = useMemo(() => parseSocietyUnits(units), [units]);

  useEffect(() => {
    if (!parsedUnits.valid) {
      setQuote(null);
      setError("");
      setLoading(false);
      return undefined;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const response = await api.get("/public/pricing/quote", {
          params: {
            duration_months: durationMonths,
            resident_portal: residentPortal,
            units: parsedUnits.units,
          },
        });
        if (active) {
          setQuote(response.data);
        }
      } catch (requestError) {
        if (active) {
          setQuote(null);
          setError(
            requestError.response?.data?.detail ||
              "We could not load the indicative pricing right now.",
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [durationMonths, parsedUnits, residentPortal]);

  const options = quote?.policy?.duration_options || [];
  const isCustom = quote?.status === "custom_quote";
  const canRequest = parsedUnits.valid && !loading && quote;

  const requestEnquiry = () => {
    if (!canRequest) {
      toast.error("Enter a valid number of flats or shops to continue.");
      return;
    }
    onRequestEnquiry(quote);
  };

  return (
    <div className="mt-12 grid gap-6 lg:grid-cols-12">
      <section
        className="border border-blue-900 bg-blue-950 p-6 text-white shadow-xl lg:col-span-7 sm:p-8"
        data-testid="launch-pricing-plans"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <span
              className={[
                "inline-flex items-center gap-2 bg-white/10 px-3 py-1 text-xs font-bold",
                "tracking-[0.14em] text-blue-100",
              ].join(" ")}
            >
              <Building2 className="h-4 w-4 text-orange-300" />
              BUDGET LAUNCH PRICING
            </span>
            <h3 className="mt-5 max-w-xl font-display text-3xl font-black sm:text-4xl">
              Budget-first ERP pricing that grows with your society.
            </h3>
          </div>
          <span
            className={[
              "border border-orange-300/40 bg-orange-400/15 px-3 py-1.5",
              "text-xs font-bold text-orange-100",
            ].join(" ")}
          >
            Launch offer
          </span>
        </div>

        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-blue-100">
          Subscription savings apply only to the progressive Society ERP subtotal. The optional
          Resident Portal and separately quoted services are never discounted by this offer.
        </p>

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {options.map((option) => (
            <DurationCard
              active={durationMonths === option.months}
              key={option.months}
              onSelect={() => setDurationMonths(option.months)}
              option={option}
            />
          ))}
        </div>

        {loading && !quote && (
          <div
            className="mt-8 border border-white/15 bg-white/10 p-5"
            data-testid="pricing-policy-loading"
          >
            Loading the current launch policy…
          </div>
        )}

        {quote?.policy && (
          <div
            className="mt-8 grid gap-4 border-t border-white/15 pt-7 sm:grid-cols-2"
            data-testid="pricing-policy-details"
          >
            <PolicyDetail
              label="Standard onboarding"
              value={onboardingRange(quote.policy.onboarding_slabs)}
            />
            <PolicyDetail
              label="Policy in use"
              value={`${quote.policy.policy_version} · effective ${quote.policy.effective_from}`}
            />
          </div>
        )}
      </section>

      <aside
        className="border border-slate-200 bg-white p-6 shadow-xl lg:col-span-5 sm:p-8"
        data-testid="launch-pricing-calculator"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold tracking-[0.14em] text-orange-600">
              INDICATIVE QUOTE
            </p>
            <h3 className="mt-2 font-display text-2xl font-black text-blue-950">
              Price your rollout
            </h3>
          </div>
          <CircleDollarSign className="h-7 w-7 text-orange-500" />
        </div>

        <div className="mt-6 space-y-5">
          <div>
            <Label className="text-sm font-bold text-slate-700" htmlFor="launch-pricing-units">
              Enrolled flats / shops
            </Label>
            <Input
              aria-describedby="launch-pricing-unit-help"
              data-testid="launch-pricing-units-input"
              id="launch-pricing-units"
              inputMode="numeric"
              min="1"
              onChange={(event) => setUnits(event.target.value)}
              type="text"
              value={units}
            />
            <p className="mt-1.5 text-xs text-slate-500" id="launch-pricing-unit-help">
              Online estimates cover up to {quote?.policy?.custom_quote_above_units || 300} flats
              or shops. Count units, not resident login accounts.
            </p>
          </div>

          <label
            className={[
              "flex cursor-pointer items-start gap-3 border border-slate-200 p-3",
              "text-sm text-slate-700",
            ].join(" ")}
          >
            <input
              checked={residentPortal}
              className="mt-0.5 h-4 w-4 accent-blue-900"
              data-testid="launch-pricing-resident-portal-toggle"
              onChange={(event) => setResidentPortal(event.target.checked)}
              type="checkbox"
            />
            <span>
              <strong className="block">Add the Resident Portal</strong>
              <span className="text-xs text-slate-600">
                Kept separate from ERP launch discounts.
              </span>
            </span>
          </label>
        </div>

        {!parsedUnits.valid && (
          <p
            className="mt-5 border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-800"
            data-testid="launch-pricing-input-error"
          >
            {parsedUnits.error}
          </p>
        )}

        {error && (
          <p
            className="mt-5 border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-800"
            data-testid="launch-pricing-api-error"
          >
            {error}
          </p>
        )}

        {isCustom && (
          <div
            className="mt-6 border border-orange-200 bg-orange-50 p-5"
            data-testid="launch-pricing-custom-quote"
          >
            <h4 className="font-display text-xl font-black text-blue-950">
              Tailored commercial quote
            </h4>
            <p className="mt-2 text-sm leading-relaxed text-slate-700">{quote.message}</p>
            <p className="mt-3 text-xs leading-relaxed text-slate-600">
              {LANDING_CONTACT.phoneDisplay} · {LANDING_CONTACT.website}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {LANDING_CONTACT.phones.map((phone) => (
                <a
                  className="border border-blue-900 px-3 py-2 text-xs font-bold text-blue-950"
                  data-testid={`launch-pricing-contact-${phone.label.replaceAll("-", "")}`}
                  href={phone.href}
                  key={phone.label}
                >
                  Call {phone.label}
                </a>
              ))}
              <a
                className="border border-blue-900 px-3 py-2 text-xs font-bold text-blue-950"
                data-testid="launch-pricing-contact-website"
                href={LANDING_CONTACT.websiteHref}
                rel="noreferrer"
                target="_blank"
              >
                Visit website
              </a>
            </div>
          </div>
        )}

        {quote?.status === "standard" && <QuoteBreakdown quote={quote} />}

        <Button
          className="mt-6 h-12 w-full bg-orange-500 text-white hover:bg-orange-600"
          data-testid="launch-pricing-enquiry-button"
          disabled={!canRequest}
          onClick={requestEnquiry}
          type="button"
        >
          {isCustom ? "Request a tailored quote" : "Book a demo with this estimate"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        <p className="mt-3 text-center text-xs leading-relaxed text-slate-500">
          Indicative preview only — this page does not create a subscription, invoice, payment or
          financial record.
        </p>
      </aside>
    </div>
  );
}

function DurationCard({ active, onSelect, option }) {
  const emphasis = option.months === 12
    ? "Most Popular"
    : option.months === 24
      ? "Maximum Savings"
      : "";
  return (
    <button
      aria-pressed={active}
      className={[
        "relative border p-4 text-left",
        "transition-[background-color,border-color,transform] duration-200",
        "hover:-translate-y-0.5",
        active
          ? "border-orange-300 bg-orange-400/15"
          : "border-white/15 bg-white/5 hover:border-white/35",
      ].join(" ")}
      data-testid={`launch-pricing-duration-${option.months}`}
      onClick={onSelect}
      type="button"
    >
      {emphasis && (
        <span
          className={[
            "absolute -top-3 left-3 bg-orange-400 px-2 py-1 text-[10px]",
            "font-black text-blue-950",
          ].join(" ")}
        >
          {emphasis}
        </span>
      )}
      <span className="block text-xs font-bold tracking-[0.12em] text-blue-100">
        {option.months} MONTHS
      </span>
      <span className="mt-3 block font-display text-3xl font-black text-white">
        {option.erp_discount_pct}%
      </span>
      <span className="mt-1 block text-xs text-blue-100">ERP subscription saving</span>
      <span className="mt-4 flex items-center gap-2 text-xs font-bold text-orange-200">
        <Sparkles className="h-3.5 w-3.5" />
        {option.onboarding_discount_pct}% onboarding saving
      </span>
    </button>
  );
}

function QuoteBreakdown({ quote }) {
  const gstLabel = quote.gst.enabled
    ? `GST at ${quote.gst.rate_pct}%`
    : "GST not currently applied";
  return (
    <div
      className="mt-6 border border-slate-200"
      data-testid="launch-pricing-quote-results"
    >
      <QuoteRow
        label="ERP subscription for selected duration"
        value={quote.erp.duration_subtotal_paise}
      />
      <QuoteRow
        label={`ERP launch saving (${quote.erp.discount_pct}%)`}
        negative
        value={quote.erp.discount_paise}
      />
      {quote.resident_portal_selected && (
        <QuoteRow
          label="Resident Portal for selected duration"
          value={quote.resident_portal.duration_subtotal_paise}
        />
      )}
      <QuoteRow
        label="Standard onboarding"
        value={quote.onboarding.standard_fee_paise}
      />
      <QuoteRow
        label={`Onboarding saving (${quote.onboarding.discount_pct}%)`}
        negative
        value={quote.onboarding.discount_paise}
      />
      <QuoteRow label={gstLabel} value={quote.gst.amount_paise} />
      <div
        className="border-t-2 border-blue-900 bg-blue-50 p-4"
        data-testid="launch-pricing-final-total"
      >
        <div className="flex items-start justify-between gap-3">
          <span className="text-sm font-bold text-blue-950">Final indicative payable</span>
          <span className="font-display text-xl font-black text-blue-950">
            {formatRupeesFromPaise(quote.final_payable_paise)}
          </span>
        </div>
        <p
          className="mt-1 text-xs font-semibold text-emerald-700"
          data-testid="launch-pricing-total-savings"
        >
          You save {formatRupeesFromPaise(quote.total_savings_paise)} with this duration.
        </p>
        <p
          className="mt-1 text-xs text-blue-800"
          data-testid="launch-pricing-monthly-equivalent"
        >
          Effective monthly equivalent: {formatRupeesFromPaise(
            Math.round(quote.final_payable_paise / quote.duration_months),
          )}
        </p>
      </div>
    </div>
  );
}

function QuoteRow({ label, negative = false, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
      <span className="text-xs text-slate-600">{label}</span>
      <span
        className={`font-mono text-sm ${negative ? "text-emerald-700" : "text-slate-800"}`}
      >
        {negative ? "−" : ""}{formatRupeesFromPaise(value)}
      </span>
    </div>
  );
}

function PolicyDetail({ label, value }) {
  return (
    <div>
      <p className="text-xs font-bold tracking-[0.12em] text-blue-200">{label}</p>
      <p
        className="mt-2 text-sm font-semibold text-white"
        data-testid={`pricing-${label.toLowerCase().replaceAll(" ", "-")}`}
      >
        {value}
      </p>
    </div>
  );
}

function onboardingRange(slabs) {
  return (slabs || [])
    .map((slab) => `${slab.from}–${slab.to}: ₹${Number(slab.amount).toLocaleString("en-IN")}`)
    .join(" · ");
}